import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import "../assets/css/Signin.css";
import axiosInstance from "../components/api/axiosInstance";
import {
  setAuthItem,
  removeAuthItem,
  persistClientPortalSession,
  persistStaffSession,
  getRememberedEmail,
  isRememberMe,
  setRememberMe,
} from "../utils/authStorage";
import { getCtxHomeUrl } from "../config/ctxPath";
import AuthShell, { AuthBrandMark } from "../components/AuthShell";
import PasswordInput from "../components/PasswordInput";
import {
  currentPortalHost,
  domainFromPortalHost,
  savePortalNotice,
  usePortalHost,
} from "../context/PortalHostContext";

/** Left-panel copy and email hints for the portal this page is served on. */
function portalBranding(portal) {
  const kind = portal?.kind;
  if (kind === "tenant") {
    const domain = domainFromPortalHost(portal.host);
    return {
      eyebrow: "Sign in to",
      title: portal.name || "Your company portal",
      subtitle: "Your company workspace on Cloud Office.",
      emailLabel: "Company Email",
      placeholder: domain ? `you@${domain}` : "you@company.com",
      hint: null,
    };
  }
  if (kind === "shared") {
    return {
      eyebrow: "Client access for",
      title: "Digix Leads CRM",
      subtitle: "Projects, leads, and your business workspace in one place.",
      emailLabel: "Email",
      placeholder: "you@example.com",
      hint: "Use the email and password shared with you by our team.",
    };
  }
  return {
    eyebrow: "Staff & client access for",
    title: "TUMMA OFFICE OS",
    subtitle: "CRM, projects, HR, finance, and daily work from one office platform.",
    emailLabel: "Company Email",
    placeholder: "you@company.com",
    hint: null,
  };
}

function Signin({ onLogin }) {
  const navigate = useNavigate();
  const portal = usePortalHost();
  const branding = portalBranding(portal);
  const [email, setEmail] = useState(
    () => new URLSearchParams(window.location.search).get("email") || getRememberedEmail()
  );
  const [password, setPassword] = useState("");
  const [remember, setRemember] = useState(() => isRememberMe());
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  // Set when the person belongs to several businesses: { data, tenants }.
  const [picker, setPicker] = useState(null);
  const [pickingId, setPickingId] = useState(null);
  // One-time token from the company switcher (same person, this company's own login).
  const [handoffToken] = useState(
    () => new URLSearchParams(window.location.search).get("handoff") || ""
  );
  const [handoffPending, setHandoffPending] = useState(Boolean(handoffToken));
  const handoffStartedRef = useRef(false);

  const persistClientSession = (data) => {
    persistClientPortalSession(data);
    window.location.href = getCtxHomeUrl();
  };

  const finishStaffLogin = (data) => {
    const { user } = data;
    persistStaffSession(data);

    const roles = Array.isArray(user?.roles) ? user.roles : [];
    // A user can hold several roles; access is the union of all their permissions.
    const merged = new Map();
    const addPerms = (list) => {
      if (!Array.isArray(list)) return;
      list.forEach((p) => {
        const module = p.Module_Name ?? p.module_name ?? p.module ?? "";
        const name = p.Permission_Name ?? p.permission_name ?? p.name ?? "";
        const id = p.Permission_Id ?? p.permission_id ?? p.id;
        const key = id != null ? `id:${id}` : `${module}|${name}`;
        if (merged.has(key)) return;
        merged.set(key, {
          Module_Name: module,
          Permission_Name: name,
          Permission_Id: id,
          module_name: module,
          permission_name: name,
          permission_id: id,
        });
      });
    };
    roles.forEach((r) => {
      addPerms(r?.permissions);
      addPerms(r?.role_permissions);
    });
    addPerms(user?.assigned_permissions);
    addPerms(user?.role_permissions);
    const permsToStore = Array.from(merged.values());
    if (Array.isArray(permsToStore) && permsToStore.length > 0) {
      setAuthItem("user_role_permissions", JSON.stringify(permsToStore));
    } else {
      removeAuthItem("user_role_permissions");
    }
    if (onLogin) onLogin();
  };

  const finishLogin = (data, { fromPasswordForm = true } = {}) => {
    if (fromPasswordForm) setRememberMe(remember, email.trim());
    if (data.account_type === "client") {
      persistClientSession(data);
    } else {
      finishStaffLogin(data);
    }
    savePortalNotice(data);
  };

  const requestSignin = (tenantId) =>
    axiosInstance.post("/signin", {
      identifier: email.trim(),
      password,
      portal_host: currentPortalHost(),
      ...(tenantId != null ? { tenant_id: tenantId } : {}),
    });

  useEffect(() => {
    if (!handoffToken || handoffStartedRef.current) return;
    handoffStartedRef.current = true;
    const url = new URL(window.location.href);
    url.searchParams.delete("handoff");
    window.history.replaceState(null, "", url.pathname + url.search + url.hash);
    axiosInstance
      .post("/linked-login-handoff/exchange", {
        token: handoffToken,
        portal_host: currentPortalHost(),
      })
      .then((res) => {
        if (res.data?.status === "success" && res.data.data) {
          finishLogin(res.data.data, { fromPasswordForm: false });
          return;
        }
        setError(res.data?.message || "Could not open this company. Please sign in.");
        setHandoffPending(false);
      })
      .catch((err) => {
        setError(signinError(err));
        setHandoffPending(false);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [handoffToken]);

  const signinError = (err) => {
    const body = err.response?.data;
    if (body?.code === "wrong_portal_host" && body.portal_url) {
      return `${body.message} (${body.portal_url.replace(/^https?:\/\//, "")})`;
    }
    return body?.message || err.message || "Login failed. Please check your credentials and try again.";
  };

  /** Companies on another portal host open there once hosts are enforced. */
  const otherHostUrl = (tenant) => {
    if (!portal?.enforced || portal.kind === "dev") return null;
    if (!tenant.Portal_Host || tenant.Portal_Host === currentPortalHost()) return null;
    return tenant.Portal_URL || `https://${tenant.Portal_Host}`;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const trimmed = email.trim();
    if (!trimmed || !password) {
      setError("Company email and password are required");
      return;
    }
    if (!trimmed.includes("@")) {
      setError("Enter a valid email address");
      return;
    }

    setError("");
    setLoading(true);
    try {
      // One login for Tumma staff, tenant owners and employees; the response decides the app.
      const response = await requestSignin();
      const data = response.data;
      if (data && data.status === "success" && data.data) {
        const tenants = Array.isArray(data.data.tenants) ? data.data.tenants : [];
        if (tenants.length > 1) {
          setPicker({ data: data.data, tenants });
        } else {
          finishLogin(data.data);
        }
      } else {
        setError(data?.message || "Login failed. Please try again.");
      }
    } catch (err) {
      setError(signinError(err));
    } finally {
      setLoading(false);
    }
  };

  const chooseBusiness = async (tenant) => {
    const signedInId = picker?.data?.tenant?.Tenant_Id;
    if (tenant.Tenant_Id === signedInId) {
      finishLogin(picker.data);
      return;
    }
    const redirect = otherHostUrl(tenant);
    if (redirect) {
      window.location.href = redirect;
      return;
    }
    setError("");
    setPickingId(tenant.Tenant_Id);
    try {
      const response = await requestSignin(tenant.Tenant_Id);
      if (response.data?.status === "success" && response.data.data) {
        finishLogin(response.data.data);
      } else {
        setError(response.data?.message || "Could not open this company.");
      }
    } catch (err) {
      setError(signinError(err));
    } finally {
      setPickingId(null);
    }
  };

  const scopeLabel = (tenant) => {
    if (tenant.Is_Platform) return "Tumma staff";
    return tenant.scope === "owner" ? "Owner" : "Employee";
  };

  if (handoffPending) {
    return (
      <AuthShell eyebrow={branding.eyebrow} title={branding.title} subtitle={branding.subtitle}>
        <AuthBrandMark logo="office" />
        <div className="signin-form">
          <p className="signin-picker-title">Opening your company...</p>
          <p className="signin-picker-hint">Signing you in to {portal?.name || "this office"}.</p>
        </div>
      </AuthShell>
    );
  }

  if (picker) {
    return (
      <AuthShell eyebrow={branding.eyebrow} title={branding.title} subtitle={branding.subtitle}>
        <AuthBrandMark logo="office" />
        {error ? <p className="signin-error">{error}</p> : null}
        <div className="signin-form">
          <p className="signin-picker-title">Choose a company</p>
          <p className="signin-picker-hint">
            You have access to {picker.tenants.length} companies. You can switch later from the header.
          </p>
          <div className="signin-picker-list">
            {picker.tenants.map((t) => (
              <button
                key={t.Tenant_Id}
                type="button"
                className="signin-picker-item"
                disabled={pickingId != null}
                onClick={() => chooseBusiness(t)}
              >
                <span className="signin-picker-name">{t.Company_Name || `Company #${t.Tenant_Id}`}</span>
                <span className="signin-picker-role">
                  {pickingId === t.Tenant_Id ? "Opening..." : scopeLabel(t)}
                  {t.Is_Default ? " · default" : ""}
                  {otherHostUrl(t) ? ` · opens ${t.Portal_Host}` : ""}
                </span>
              </button>
            ))}
          </div>
          <button
            type="button"
            className="signin-forgot-btn"
            onClick={() => {
              setPicker(null);
              setError("");
            }}
          >
            Use a different account
          </button>
        </div>
      </AuthShell>
    );
  }

  const unknownHostBlocked = portal?.enforced && portal.kind === "unknown";

  return (
    <AuthShell eyebrow={branding.eyebrow} title={branding.title} subtitle={branding.subtitle}>
      <AuthBrandMark logo="office" />

      {portal?.kind === "tenant" && portal.name ? (
        <p className="signin-portal-name">{portal.name}</p>
      ) : null}
      {unknownHostBlocked ? (
        <p className="signin-error">
          This address is not a Cloud Office portal. Clients without their own domain sign in at{" "}
          <a href={`https://${portal.shared_host}`}>{portal.shared_host}</a>.
        </p>
      ) : null}
      {error ? <p className="signin-error">{error}</p> : null}

      <form onSubmit={handleSubmit} className="signin-form">
        <div className="signin-field">
          <label htmlFor="signin-company-email">{branding.emailLabel}</label>
          <input
            id="signin-company-email"
            type="email"
            value={email}
            onChange={(e) => {
              setEmail(e.target.value);
              setError("");
            }}
            required
            autoComplete="username"
            placeholder={branding.placeholder}
          />
          {branding.hint ? <small className="signin-field-hint">{branding.hint}</small> : null}
        </div>
        <div className="signin-field">
          <label htmlFor="signin-password">Password</label>
          <PasswordInput
            id="signin-password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
            autoComplete="current-password"
            placeholder="Enter your password"
          />
        </div>
        <div className="signin-forgot signin-options">
          <label className="signin-remember">
            <input type="checkbox" checked={remember} onChange={(e) => setRemember(e.target.checked)} />
            Remember me
          </label>
          <button
            type="button"
            className="signin-forgot-btn"
            onClick={() => navigate("/forgot-password")}

          >
            Forgot Password?
          </button>
        </div>
        <button type="submit" className="signin-submit" disabled={loading || unknownHostBlocked}>
          {loading ? "Signing in..." : "Sign In"}
        </button>
      </form>
    </AuthShell>
  );
}

export default Signin;
